import { ContentsCountProps } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Types'
import { FieldArray, useField } from 'formik'
import { CountInput } from 'components/Wizards/CountInput/CountInput'
import Styles from './ContentCount.module.scss'
import { ReactElement, isValidElement } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { pluralize } from 'utils'
import { capitalize, lowerCase } from 'lodash'
import { Icon } from 'blaze-ui/icons/Types'
import { filterDOMProps, useId } from '@react-aria/utils'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { AriaLabelingProps } from '@react-types/shared'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'

const getContentTypeLabel = (type: string): string => {
  const typeMapping: Record<string, string> = {
    post: 'Posts',
    story: 'Stories',
    blog_post: 'Posts',
    newsletter: 'Emails',
    short: 'Shorts',
    reel: 'Reels',
  }

  return typeMapping[type] || pluralize(capitalize(type), 2)
}

export const getLabel = (type: string, customLabel?: string | null, label?: string) => {
  if (customLabel !== undefined) {
    return customLabel
  }
  if (label?.trim()) {
    return `${label} ${pluralize(lowerCase(type.replace('blog_', '')), 2)}`
  }
  return getContentTypeLabel(type)
}

export interface ContentCountProps extends AriaLabelingProps {
  value: number
  name: string
  onChange: (value: number) => void
  label?: string | null
  icon?: Icon | ReactElement
  isDisabled?: boolean
  weekLabel?: string | null
  buttonVariant?: ButtonProps['variant']
  isFullyGreyedOut?: boolean
  maxPostsPerChannel?: number
  lockButton?: ReactElement
  withBorder?: boolean
}

export const ContentCount = ({
  value,
  name,
  onChange,
  label,
  icon,
  isDisabled = false,
  buttonVariant = 'ghost',
  weekLabel = 'week',
  isFullyGreyedOut = false,
  maxPostsPerChannel,
  lockButton,
  withBorder = false,
  ...labellingProps
}: ContentCountProps) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { isMobile } = useResponsiveDesign()
  const id = useId()

  const isReactElement = isValidElement(icon)
  const IconComponent = isReactElement ? null : (icon as Icon)

  // Check if we're at the post limit and should show upgrade button
  const isAtPostLimit = maxPostsPerChannel !== undefined && value >= maxPostsPerChannel

  const desktopButtonVariant = isOnboardingRedesign ? 'secondary' : buttonVariant

  return (
    <div
      className={Styles.count}
      role="group"
      aria-labelledby={label ? id : undefined}
      {...filterDOMProps(labellingProps, { labelable: true })}
    >
      <div className={cx(Styles.type, { [Styles.empty]: value === 0, [Styles.greyedOut]: isFullyGreyedOut })}>
        <div className={Styles.details}>
          {isReactElement ? icon : IconComponent && <IconComponent size={20} />}
          <div className={Styles.label}>
            {isOnboardingRedesign ? (
              <Text>Posts per week</Text>
            ) : (
              <>
                {label && (
                  <Text variant="smallList" id={id}>
                    {label}
                  </Text>
                )}
                {weekLabel && (
                  <Text variant="secondary" color="var(--dark-40)">
                    / {weekLabel}
                  </Text>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <CountInput
        buttonVariant={isMobile ? 'ghost-tertiary' : desktopButtonVariant}
        flex
        size={isOnboardingRedesign ? 'sm' : 'md'}
        buttonSize={isOnboardingRedesign ? 'sm' : 'xs'}
        name={name}
        value={value}
        withBorder={withBorder || isOnboardingRedesign}
        min={0}
        max={15}
        onChange={onChange}
        disabled={isDisabled}
        className={cx(Styles.countInput, { [Styles.empty]: value === 0, [Styles.greyedOut]: isFullyGreyedOut })}
        customPlusButton={isAtPostLimit && lockButton ? lockButton : undefined}
        applyRedesign={isOnboardingRedesign}
      />
    </div>
  )
}

export const ContentsCount = ({
  name,
  label,
  icon,
  isDisabled = false,
  customLabel,
  customCount,
  buttonVariant,
  weekLabel = 'week',
  isFullyGreyedOut = false,
  maxPostsPerChannel,
  lockButton,
  withBorder,
  ...labellingProps
}: ContentsCountProps & {
  customLabel?: string | null
  customCount?: number
  maxPostsPerChannel?: number
}) => {
  const [field] = useField<[{ type: string; count: number }]>(name)

  return (
    <FieldArray name={field.name}>
      {({ replace }) => {
        return (
          <>
            {field.value.map((value, index) => {
              const displayValue = customCount !== undefined && isDisabled ? customCount : value.count

              return (
                <ContentCount
                  key={index}
                  label={getLabel(value.type, customLabel, label)}
                  icon={icon}
                  name={field.name}
                  value={displayValue}
                  onChange={(newValue) => replace(index, { ...value, count: newValue })}
                  isDisabled={isDisabled}
                  buttonVariant={buttonVariant}
                  weekLabel={weekLabel}
                  isFullyGreyedOut={isFullyGreyedOut}
                  maxPostsPerChannel={maxPostsPerChannel}
                  lockButton={lockButton}
                  withBorder={withBorder}
                  {...filterDOMProps(labellingProps, { labelable: true })}
                />
              )
            })}
          </>
        )
      }}
    </FieldArray>
  )
}
